import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from "@angular/core";
import { ComparisonExpression, Expression, Predicate } from "../model";
import {
    ComparatorPicker,
    ComparatorPickerBuilder,
    ComparisonOperator,
    Field,
    FormDefinition,
} from "../../vforms-model";
import { TranslocoService } from "@jsverse/transloco";
import { TextBoxComparatorPickerBuilder, TextBoxType } from "../../field-types/text-box/text-box";
import { CheckboxComparatorPickerBuilder, CheckBoxType } from "../../field-types/check-box/check-box";
import { MultipleSelectionListPickerBuilder, SingleSelectionListPickerBuilder } from "../../field-types/selection-list";
import { SingleSelectionListType } from "../../field-types/single-selection-list/single-selection-list";
import { SingleSelectionDropdownType } from "../../field-types/single-selection-dropdown/single-selection-dropdown";
import { DatetimeComparatorPickerBuilder, DatetimeType } from "../../field-types/datetime/datetime";
import { TextareaType } from "../../field-types/textarea/text-area";
import { MultipleSelectionDropdownType } from "../../field-types/multiple-selection-dropdown/multiple-selection-dropdown";
import { MultipleSelectionListType } from "../../field-types/multiple-selection-list/multiple-selection-list";
import { EmailFieldType } from "../../field-types/email/email";
import { NumberComparatorPickerBuilder, NumberFieldType } from "../../field-types/number/number";
import { Cid10PickerBuilder, Cid10Type } from "../../field-types/cid10/cid10";
import { FileUploaderPickerBuilder, FileUploaderType } from "../../field-types/file-uploader/file-uploader";

@Component({
    selector: "predicate-component",
    templateUrl: "./predicate.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PredicateComponent {
    @Input()
    formDefinition: FormDefinition;

    @Input()
    predicate: Predicate;

    comparatorPickerBuilders: Map<string, ComparatorPickerBuilder>;

    constructor(private _changeDetector: ChangeDetectorRef, private _transolocoService: TranslocoService) {
        this.comparatorPickerBuilders = new Map<string, ComparatorPickerBuilder>();
        this.comparatorPickerBuilders.set(CheckBoxType.Id, new CheckboxComparatorPickerBuilder());
        this.comparatorPickerBuilders.set(TextBoxType.Id, new TextBoxComparatorPickerBuilder());
        this.comparatorPickerBuilders.set(TextareaType.Id, new TextBoxComparatorPickerBuilder());
        this.comparatorPickerBuilders.set(EmailFieldType.Id, new TextBoxComparatorPickerBuilder());
        this.comparatorPickerBuilders.set(NumberFieldType.Id, new NumberComparatorPickerBuilder());
        this.comparatorPickerBuilders.set(SingleSelectionListType.Id, new SingleSelectionListPickerBuilder());
        this.comparatorPickerBuilders.set(SingleSelectionDropdownType.Id, new SingleSelectionListPickerBuilder());
        this.comparatorPickerBuilders.set(MultipleSelectionListType.Id, new MultipleSelectionListPickerBuilder());
        this.comparatorPickerBuilders.set(MultipleSelectionDropdownType.Id, new MultipleSelectionListPickerBuilder());
        this.comparatorPickerBuilders.set(DatetimeType.Id, new DatetimeComparatorPickerBuilder());
        this.comparatorPickerBuilders.set(FileUploaderType.Id, new FileUploaderPickerBuilder());
        this.comparatorPickerBuilders.set(Cid10Type.Id, new Cid10PickerBuilder());
    }

    add() {
        this.predicate.newExpression();
        this._changeDetector.markForCheck();
    }

    newCondition(expression: Expression) {
        expression.newCondition();
        this._changeDetector.markForCheck();
    }

    removeCondition(expression: Expression, condition: ComparisonExpression) {
        this.predicate.removeCondition(expression, condition);
        this._changeDetector.markForCheck();
    }

    getAvailableFields(condition: ComparisonExpression, expression: Expression) {
        let fields = [];
        for (let field of this.formDefinition.fields) {
            let exists = expression.conditions.findIndex((c) => c !== condition && c.field?.id === field.id) >= 0;
            if (!exists) {
                fields.push(field);
            }
        }
        return fields;
    }

    getOperatorText(field: Field, operator: ComparisonOperator) {
        let key = `expression-settings.${field.type.id}.${operator}`;
        let text = this._transolocoService.translate(key);
        if (text === key) {
            text = this._transolocoService.translate(`expression-settings.${operator}`);
        }
        return text;
    }

    getComparatorPicker(e: ComparisonExpression): ComparatorPicker {
        return this.comparatorPickerBuilders.get(e.field?.type.id).build(e);
    }
}
